import { proxyUrl } from "@/config";
import { requestIdentityOrchestrator } from "@/services/UserIdentitiesService";
import type { IdentityOrchestratorEnvelope } from "@/services/UserIdentitiesService";

/** Datos del alta de representante legal (CreateLegalRepresentativeDto en IroniaApi). */
export interface CreateLegalRepresentativeRequest
{
    firstName: string;
    lastName: string;
    secondLastName: string | null;
    idType: "DNI" | "NIE";
    idDocument: string;
    birthday: string;
    idExpiration: string | null;
    hasPermanentIdExpiration: boolean;
    nationalCountry: string;
    email: string;
    countryPrefix: string;
    phone: string;
    postalAddress:
    {
        roadType: string | null;
        streetName: string;
        streetNumber: string | null;
        streetFloor: string | null;
        streetDoor: string | null;
        streetOther: string | null;
        zipCode: string | null;
        city: string;
        state: string | null;
        country: string;
    };
    fatcaStatus: number;
}

export interface LegalRepresentativeResultDto
{
    userIdentityId: number;
    crmCustomerId: number | null;
    finametrixId: string | null;
    status: string;
    fullName: string;
}

/** Empresa titular de la suscripción (CreateCompanyDraftDto en IroniaApi). */
export interface CreateCompanyDraftRequest
{
    businessName: string;
    taxId: string;
    incorporationDate: string;
    email: string;
    countryPrefix: string;
    phone: string;
}

export interface CompanyDraftResultDto
{
    companyIdentityId: number;
    companyDetailsId: number;
    businessName: string;
    taxId: string;
    companyDetailsType: "Company" | "JointOwnership";
    status: string;
}

/** Domicilio de la empresa (CompanyAddressDto en IroniaApi). */
export interface CompanyAddressDto
{
    country: string | null;
    zipCode: string | null;
    city: string | null;
    roadType: string | null;
    streetName: string | null;
    streetNumber: string | null;
    streetFloor: string | null;
    streetDoor: string | null;
    streetOther: string | null;
}

/** Rol de Omnia del representante y firma (LegalRepresentativeSigns). */
export type LegalRepresentativeRole = "TitularReal" | "ApoderadoLegal";
export type LegalRepresentativeSign = "NoSignature" | "Joint" | "Solidary";

export interface CompanyLegalRepresentativeInput
{
    userIdentityId: number;
    role: LegalRepresentativeRole | null;
    sign: LegalRepresentativeSign | null;
    ownership: number | null;
}

export interface CompanyLegalRepresentativeDto extends CompanyLegalRepresentativeInput
{
    fullName: string | null;
    maskedIdDocument: string | null;
}

// ProofOfIncome: pruebas de ingresos del KYC (paso 4)
export type CompanyDocumentCategory = "Incorporation" | "TaxIdCard" | "ProofOfIncome";

export interface CompanyDocumentDto
{
    id: number;
    category: CompanyDocumentCategory;
    fileName: string;
    createAt: string;
}

/** Datos del paso 3 comunes a la petición y a la respuesta. */
export interface CompanyDetailsFields
{
    businessName: string | null;
    sector: string | null;
    cnae: string | null;
    incorporationDate: string | null;
    incorporationCountry: string | null;
    incorporationPlace: string | null;
    nationalCountry: string | null;
    numberOfEmployees: string | null;
    lei: string | null;
    leiExpirationDate: string | null;
    numCnmv: string | null;
    sicav: string | null;
    digitalSignature: boolean | null;
    commercialRegistry: string | null;
    registryProvince: string | null;
    volume: number | null;
    page: number | null;
    sheet: string | null;
    protocol: string | null;
    notary: string | null;
    fiscalAddress: CompanyAddressDto | null;
    registeredAddressSameAsFiscal: boolean;
    registeredAddress: CompanyAddressDto | null;
    email: string | null;
    countryPrefix: string | null;
    phone: string | null;
}

/** PUT companies/companyDraft/{id} (SaveCompanyDetailsDto). complete = true para continuar al KYC. */
export interface SaveCompanyDetailsRequest extends CompanyDetailsFields
{
    legalRepresentatives: CompanyLegalRepresentativeInput[];
    complete: boolean;
}

/** GET companies/companyDraft/{id} (CompanyDetailsDraftDto). */
export interface CompanyDetailsDraftDto extends CompanyDetailsFields
{
    companyIdentityId: number;
    taxId: string;
    status: string;
    legalRepresentatives: CompanyLegalRepresentativeDto[];
    documents: CompanyDocumentDto[];
}

/** Datos del KYC de la empresa (paso 4) comunes a la petición y a la respuesta. Mismos nombres y literales que Omnia. */
export interface CompanyKycFields
{
    isPep: boolean;
    pepFullName: string | null;
    pepPosition: string | null;
    isPepRelative: boolean;
    pepRelativeFullName: string | null;
    pepRelativePosition: string | null;
    pepRelativeRelationship: string | null;
    riskCountries: boolean;
    riskCountriesWhich: string[];
    listedCompany: boolean;
    isBranchOrSubsidiary: boolean;
    bearerShares: boolean;
    economicActivity: string | null;
    fundsOrigin: string[];
    fundsOriginInvestmentsWhich: string | null;
    fundsOriginOther: string | null;
    annualGrossIncome: string | null;
    portfolioVolume: number | null;
    maxContribution: number | null;
    providesFundsDocumentation: boolean;
    knowsManager: boolean;
    relationshipOpeningReasons: string | null;
    usCitizen: boolean;
    usPersonControl: boolean;
    usResidence: boolean;
    usTaxObligation: boolean;
    usBeneficiary: boolean;
    usTin: string | null;
    dataTruthfulnessAccepted: boolean;
}

/** PUT companies/companyDraft/{id}/kyc. complete = true para enviar a bastanteo. */
export interface SaveCompanyKycRequest extends CompanyKycFields
{
    complete: boolean;
}

/** GET companies/companyDraft/{id}/kyc (CompanyKycDraftDto). documents: solo las pruebas de ingresos. */
export interface CompanyKycDraftDto extends CompanyKycFields
{
    companyIdentityId: number;
    taxId: string;
    businessName: string | null;
    status: string;
    documents: CompanyDocumentDto[];
}

/** POST companies/companyDraft/{id}/submit: la empresa enviada a Omnia queda en PENDING. */
export interface CompanySubmissionResultDto
{
    companyIdentityId: number;
    status: string;
}

/** Interviniente que se puede elegir como representante (propio o prestado). */
export interface SelectableRepresentativeDto
{
    userIdentityId: number;
    fullName: string;
    maskedIdDocument: string | null;
    isOwn: boolean;
}

class CompanyOnboardingService
{
    // POST companies/companyDraft: paso 2, guarda la empresa como borrador antes del pago
    public createCompanyDraft = async (data: CreateCompanyDraftRequest): Promise<IdentityOrchestratorEnvelope<CompanyDraftResultDto>> =>
    {
        return requestIdentityOrchestrator<CompanyDraftResultDto>(
        {
            method: "POST",
            url: `${proxyUrl}/companies/companyDraft`,
            data,
        });
    };

    // GET companies/companyDraft/{id}: paso 3, la empresa en borrador con lo guardado
    public getCompanyDraft = async (companyIdentityId: number): Promise<IdentityOrchestratorEnvelope<CompanyDetailsDraftDto>> =>
    {
        return requestIdentityOrchestrator<CompanyDetailsDraftDto>({method: "GET", url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}`});
    };

    // PUT companies/companyDraft/{id}: guarda el paso 3 (borrador o, con complete, para continuar al KYC)
    public saveCompanyDetails = async (companyIdentityId: number, data: SaveCompanyDetailsRequest): Promise<IdentityOrchestratorEnvelope<CompanyDetailsDraftDto>> =>
    {
        return requestIdentityOrchestrator<CompanyDetailsDraftDto>({method: "PUT", url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}`, data});
    };

    // GET companies/companyDraft/{id}/kyc: paso 4, el KYC de la empresa con lo guardado
    public getCompanyKyc = async (companyIdentityId: number): Promise<IdentityOrchestratorEnvelope<CompanyKycDraftDto>> =>
    {
        return requestIdentityOrchestrator<CompanyKycDraftDto>({method: "GET", url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}/kyc`});
    };

    // PUT companies/companyDraft/{id}/kyc: guarda el paso 4 (borrador o, con complete, para enviar a bastanteo)
    public saveCompanyKyc = async (companyIdentityId: number, data: SaveCompanyKycRequest): Promise<IdentityOrchestratorEnvelope<CompanyKycDraftDto>> =>
    {
        return requestIdentityOrchestrator<CompanyKycDraftDto>({method: "PUT", url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}/kyc`, data});
    };

    // POST companies/companyDraft/{id}/submit: "Enviar a bastanteo", alta de la empresa en Omnia con todo lo guardado
    public submitCompany = async (companyIdentityId: number): Promise<IdentityOrchestratorEnvelope<CompanySubmissionResultDto>> =>
    {
        return requestIdentityOrchestrator<CompanySubmissionResultDto>({method: "POST", url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}/submit`});
    };

    // GET companies/selectableRepresentatives: intervinientes propios y prestados que pueden ser representantes
    public getSelectableRepresentatives = async (): Promise<IdentityOrchestratorEnvelope<SelectableRepresentativeDto[]>> =>
    {
        return requestIdentityOrchestrator<SelectableRepresentativeDto[]>({method: "GET", url: `${proxyUrl}/companies/selectableRepresentatives`});
    };

    // POST companies/companyDraft/{id}/documents: multipart con el fichero y su categoría
    public uploadCompanyDocument = async (companyIdentityId: number, category: CompanyDocumentCategory, file: File): Promise<IdentityOrchestratorEnvelope<CompanyDocumentDto>> =>
    {
        const formData = new FormData();
        formData.append("category", category);
        formData.append("file", file);

        return requestIdentityOrchestrator<CompanyDocumentDto>(
        {
            method: "POST",
            url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}/documents`,
            data: formData,
            headers: { "content-type": "multipart/form-data" },
        });
    };

    // DELETE companies/companyDraft/{id}/documents/{documentId}
    public deleteCompanyDocument = async (companyIdentityId: number, documentId: number): Promise<IdentityOrchestratorEnvelope<boolean>> =>
    {
        return requestIdentityOrchestrator<boolean>({method: "DELETE", url: `${proxyUrl}/companies/companyDraft/${companyIdentityId}/documents/${documentId}`});
    };

    // POST companies/legalRepresentative (function app Companies): multipart con data + anverso y reverso del DNI
    public createLegalRepresentative = async (data: CreateLegalRepresentativeRequest, idFront: File, idBack: File): Promise<IdentityOrchestratorEnvelope<LegalRepresentativeResultDto>> =>
    {
        const formData = new FormData();
        formData.append("data", JSON.stringify(data));
        formData.append("idFront", idFront);
        formData.append("idBack", idBack);

        return requestIdentityOrchestrator<LegalRepresentativeResultDto>(
        {
            method: "POST",
            url: `${proxyUrl}/companies/legalRepresentative`,
            data: formData,
            headers: { "content-type": "multipart/form-data" },
        });
    };
}

const companyOnboardingService = new CompanyOnboardingService();
export default companyOnboardingService;
